import { createSelector } from 'reselect'
import { get, map, isEmpty } from 'lodash-es'
import { decamelize } from 'humps'
import { format, addMonths, lastDayOfMonth, startOfMonth } from 'date-fns'
import { partyApiSelectors } from '../../api/party'
import * as invoiceApiSelectors from '../../api/invoice/selectors'
import { getPortfolioById, getPrimaryTenantByPortfolioId } from '../../api/portfolio/selectors'
import { currencyUtils } from '../../../utils'
import { portfolioApiSelectors, portfolioApiUtils } from '../../api/portfolio'
import { StaticStepIcons, createCountBadgeIcon, ContactIcon, ContactsIcon, StatementIcon } from './wizardStepIcons'
import { $TSFixMe } from 'types/ts-migrate'
import { PortfolioWizardStep, PortfolioWizardStepNames } from './types'
import { getStep } from './steps'
// Additional imports for dialog-specific selectors
import { propertyApiSelectors } from '../../api/property'
import { searchPlaceDetails } from 'modules/api/property/utils'
import { titleCase } from 'utils/string'
import * as agencyApiSelectors from '../../api/agency/selectors'
import { agencyApiSelectors as agencyApiSelectorsNamed } from 'modules/api/agency'
import { EMPTY_AGENCY_PROFILE_FORM_VALUES } from 'modules/api/agency/constants'
import { TAgencyProfileFormValues } from 'modules/api/agency/types'
import { userApiSelectors } from '../../api/user'
import { commissionUndertakings, leaseTypes } from 'modules/api/portfolio/constants'
import { getDepositBeneficiaryAmount } from 'modules/api/invoice/utils'

export const state = (state: any): $TSFixMe => state
export const chatbotDialogs = (state: any): $TSFixMe => state.chatbot.dialogs
export const chatbotPortfolio = (state: any): $TSFixMe => state.chatbot.portfolio
export const chatbotForms = createSelector(state, s => s.chatbot.forms)

export const getLeaseTypeSelectionDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseTypeSelection)
export const getPropertyDetailsDialog = createSelector(chatbotDialogs, dialogs => dialogs.propertyDetails)
export const getOwnerDetailsDialog = createSelector(chatbotDialogs, dialogs => dialogs.ownerDetails)
export const getTenantDetailsDialog = createSelector(chatbotDialogs, dialogs => dialogs.tenantDetails)
export const getLeaseTermDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseTerm)
export const getLeaseRentAndFeesDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseRentAndFees)
export const getLeaseSettingsDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseSettings)
export const getLeaseCommissionDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseCommission)
export const getLeaseInvoicesDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseInvoices)
export const getLeaseConfirmationDialog = createSelector(chatbotDialogs, dialogs => dialogs.leaseConfirmation)

// Onboarding
export const getCurrentUserDetailsDialog = createSelector(chatbotDialogs, dialogs => dialogs.userDetails)
export const getAgencyDetailsDialog = createSelector(chatbotDialogs, dialogs => dialogs.agencyDetails)

export const getCurrentPortfolioId = createSelector(chatbotPortfolio, s => get(s, 'currentPortfolioId'))
export const isActivationConfirmationOpen = createSelector(chatbotPortfolio, s => get(s, 'activationConfirmationOpen'))

export const getDialogChanges = createSelector(
  chatbotDialogs,
  dialogs => (dialog: any) => get(dialogs, [dialog, 'changes'], {}),
)

export const hasSelectedLeaseType = createSelector(
  getLeaseTypeSelectionDialog,
  leaseTypeDialog => !!get(leaseTypeDialog, 'leaseType', false),
)

export const getCurrentPortfolioStatus = createSelector(state, state => {
  const portfolio = getPortfolioById(state)(getCurrentPortfolioId(state))
  if (portfolio === undefined || isEmpty(portfolio)) {
    return 'in progress'
  }
  return get(portfolio, 'tags.status', '')
})

export const isCurrentPortfolioDraft = createSelector(state, s =>
  portfolioApiSelectors.isDraftPortfolio(s)(getCurrentPortfolioId(s)),
)

const hasSelectedProperty = createSelector(
  getPropertyDetailsDialog,
  dialog => !!(dialog.selectedProperty || dialog.selectedPlace),
)

// `partyIds` is a flat array of party ID strings (from getAllTenantPartyIdsByPortfolioId).
const getPartyEmails = (state: any, partyIds: any): string => {
  const emails = partyIds.map((partyId: any) => {
    return partyApiSelectors.getPartyEmailById(state)(partyId)
  })
  return emails.filter(Boolean).join(', ')
}
const getPartyAlternativeEmails = (state: any, partyIds: any): string => {
  const emails = partyIds.map((partyId: any) => {
    return partyApiSelectors.getPartyAlternativeEmailById(state)(partyId)
  })
  return emails.filter(Boolean).join(', ')
}

export const getActivationItems = createSelector(state, state => {
  const currentPortfolioId = getCurrentPortfolioId(state)
  const isUnmanagedLease = getCurrentLeaseType(state) === leaseTypes.unmanaged
  const primaryTenantId = getPrimaryTenantByPortfolioId(state)(currentPortfolioId)
  const tenantEmail: string = partyApiSelectors.getPartyEmailById(state)(primaryTenantId)
  const tenantAlternativeEmail = partyApiSelectors.getPartyAlternativeEmailById(state)(primaryTenantId)
  const leaseInvoices = getLeaseInvoicesDialog(state)?.invoices || []
  const tenants = portfolioApiSelectors.getAllTenantPartyIdsByPortfolioId(state)(currentPortfolioId)
  const agents = portfolioApiSelectors.getAllCommissionSplitAgentIdsByPortfolioId(state)(currentPortfolioId)
  const tenantCount: number = tenants.length
  const agentCount: number = agents.length
  const tenantEmails: string = getPartyEmails(state, tenants)
  const tenantAlternativeEmails: string = getPartyAlternativeEmails(state, tenants)
  const items = []

  tenantCount === 1
    ? items.push({
        type: 'email',
        mainText: `Send lease activation email to tenant`,
        secondaryText: `Recipient: ${tenantEmail}`,
        tertiaryText: tenantAlternativeEmail ? `CC: ${tenantAlternativeEmail}` : '',
        checked: true,
      } as never)
    : items.push({
        type: 'email',
        mainText: `Send lease activation email to tenants`,
        secondaryText: `Recipients: ${tenantEmails}`,
        tertiaryText: tenantAlternativeEmails ? `CC: ${tenantAlternativeEmails}` : '',
        checked: true,
      } as never)

  if (agentCount > 0) {
    const [firstAgentId, ...restAgentIds] = agents
    const firstAgentEmail: string = partyApiSelectors.getPartyEmailById(state)(firstAgentId)
    const ccAgentEmails: string = restAgentIds
      .map((id: string) => partyApiSelectors.getPartyEmailById(state)(id))
      .filter(Boolean)
      .join(', ')
    items.push({
      type: 'email',
      mainText: `Send lease activation email to agent${agentCount > 1 ? 's' : ''}`,
      secondaryText: `Recipient: ${firstAgentEmail}`,
      tertiaryText: agentCount > 1 && ccAgentEmails ? `CC: ${ccAgentEmails}` : '',
      checked: true,
    } as never)
  }

  leaseInvoices.forEach((inv: any) => {
    const category: string =
      invoiceApiSelectors.getInvoiceTypeNameByValue(state)(inv.category) || decamelize(inv.category, { separator: ' ' })
    const formattedAmount: string = inv.netAmount ? currencyUtils.formatCurrency(inv.netAmount) : 'Variable'

    // Every draft invoice — once-off or recurring — is generated on activation and is listed
    // by name and amount only. Unmanaged leases have no recurring invoices; on managed leases
    // the recurring ones are badged and explained by the summary's "Monthly" note.
    items.push({
      type: 'invoice',
      mainText: category,
      secondaryText: formattedAmount,
      recurring: !isUnmanagedLease && inv.interval !== 'OnceOff',
      checked: true,
    } as never)
  })
  return items
})

const getOwnerDetailsProgress = createSelector(state, s => {
  const ownerDialog = getOwnerDetailsDialog(s)
  return ownerDialog.selectedParty ? 100 : 0
})

const getApplicationsProgress = createSelector(state, s => (portfolioId: string) => {
  const applications = portfolioApiSelectors.getApplicationsByPortfolioId(s)(portfolioId)
  if (applications?.find(application => application.status === 'Accepted')) {
    return 100
  } else if (!isEmpty(applications)) {
    return 50
  } else {
    return 0
  }
})

export const isAnApplicationAccepted = createSelector(state, s => {
  const currentPortfolioId = getCurrentPortfolioId(s)
  const applications = portfolioApiSelectors.getApplicationsByPortfolioId(s)(currentPortfolioId)
  return applications?.find(application => application.status === 'Accepted') !== undefined
})

const getTenantDetailsProgress = createSelector(state, s => {
  const tenantsDialog = getTenantDetailsDialog(s)
  return tenantsDialog.selectedParty ? 100 : 0
})

export const isThereAnExistingTenant = createSelector(state, s => {
  const currentPortfolioId = getCurrentPortfolioId(s)
  const tenants = portfolioApiSelectors.getAllTenantPartyIdsByPortfolioId(s)(currentPortfolioId)
  return tenants.length > 0
})

const getLeaseTermsProgress = createSelector(state, s => {
  const { term } = getLeaseTermDialog(s)
  if (!term) {
    return 0
  }
  // Only the date fields are required to complete the step. `rolloverMonthToMonth`
  // is an optional boolean toggle that is valid either way, so it must not count
  // toward progress — otherwise leaving it off (the default) caps the step at ~67%.
  const requiredFields = [term.startDate, term.endDate]
  const completedFields = requiredFields.filter(Boolean).length
  return (completedFields / requiredFields.length) * 100
})

const getLeaseRentProgress = createSelector(state, s => {
  const { monthlyRentAmount } = getLeaseRentAndFeesDialog(s)
  // Monthly rent is the only required field in the Rent step; deposits, fees and
  // the depositHeldByLandlord toggle are all optional, so they must not count
  // toward progress (a valid rent-only config should read 100%, and the boolean
  // toggle must not permanently cap it below 100).
  return parseFloat(monthlyRentAmount) > 0 ? 100 : 0
})

export const getPortfolioNavbarItems = createSelector(state, (state): PortfolioWizardStep[] => {
  const currentPortfolio = getCurrentPortfolioId(state)
  const hasSavedState = (dialog: any): $TSFixMe => dialogSavedStateSelector(state)(dialog) !== undefined
  const isDraftPortfolio = portfolioApiSelectors.isDraftPortfolio(state)(currentPortfolio)
  const isUnmanagedLease = getCurrentLeaseType(state) === leaseTypes.unmanaged
  const isPortfolioTerminated = portfolioApiSelectors.isPortfolioTerminated(state)(currentPortfolio)
  const isPortfolioExpired = portfolioApiSelectors.isPortfolioExpired(state)(currentPortfolio)
  const allLandlords = portfolioApiSelectors.getAllOwnerPartyIdsByPortfolioId(state)(currentPortfolio)
  const allApplications = portfolioApiSelectors.getAllApplicationsByPortfolioId(state)(currentPortfolio)
  const allTenants = portfolioApiSelectors.getAllTenantPartyIdsByPortfolioId(state)(currentPortfolio)
  const portfolio = getPortfolioById(state)(currentPortfolio) as any
  const hasLeaseTermsData = Boolean(get(portfolio, 'leaseTerms.startDate'))
  const hasRentData = portfolioApiSelectors.getMonthlyRentAmount(state)(currentPortfolio) > 0
  const commission = portfolioApiSelectors.getCommissionByPortfolioId(state)(currentPortfolio)
  const hasCommissionData = Boolean(get(commission, 'managementFee') || get(commission, 'procurementFee'))
  const hasInvoicesData =
    (portfolioApiSelectors.getInvoiceTemplatesByPortfolioId(state)(currentPortfolio) || []).length > 0
  const hasSettingsData = Boolean(get(portfolio, 'settings'))
  /**
   * @todo temp code please remove
   * allows the property button to get marked off
   */
  const hasProperty = getPropertyDetailsDialog(state).selectedProperty !== null
  const items: PortfolioWizardStep[] = [
    {
      name: PortfolioWizardStepNames.type,
      route: '/leases/:id/edit/type',
      label: 'Lease type',
      activeWhen: isDraftPortfolio ? ['/leases/new/edit'] : ['/leases/:id/edit/type'],
      hasCheckmark: hasProperty,
      progress: hasProperty ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.type]!,
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.property,
      route: '/leases/:id/edit/property',
      label: 'Property',
      activeWhen: ['/leases/:id/edit/property'],
      hasCheckmark: hasSavedState('propertyDetails') || hasProperty,
      progress: hasProperty ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.property]!,
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.owners,
      route: '/leases/:id/edit/property/owner',
      label: 'Owners',
      hasCheckmark: hasSavedState('ownerDetails') || allLandlords.length > 0,
      progress: getOwnerDetailsProgress(state),
      Icon: createCountBadgeIcon(ContactIcon, allLandlords.length),
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.applications,
      route: '/leases/:id/edit/applications',
      label: 'Applications',
      hasCheckmark: getApplicationsProgress(state)(currentPortfolio) === 100,
      isOptional: true,
      progress: getApplicationsProgress(state)(currentPortfolio),
      Icon: createCountBadgeIcon(StatementIcon, allApplications.length),
      isVisible: !isDraftPortfolio && allApplications.length === 0 ? false : true,
    },
    {
      name: PortfolioWizardStepNames.tenants,
      route: '/leases/:id/edit/tenants',
      label: 'Tenants',
      hasCheckmark: hasSavedState('tenantDetails') || allTenants.length > 0,
      progress: getTenantDetailsProgress(state),
      Icon: createCountBadgeIcon(ContactsIcon, allTenants.length),
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.lease,
      route: '/leases/:id/edit/term',
      label: 'Lease term',
      hasCheckmark: hasSavedState('leaseTerm') || hasLeaseTermsData,
      progress: getLeaseTermsProgress(state),
      Icon: StaticStepIcons[PortfolioWizardStepNames.lease]!,
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.rent,
      route: '/leases/:id/edit/rent-and-fees',
      label: 'Rent',
      hasCheckmark: hasSavedState('leaseRentAndFees') || hasRentData,
      progress: getLeaseRentProgress(state),
      Icon: StaticStepIcons[PortfolioWizardStepNames.rent]!,
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.commission,
      route: '/leases/:id/edit/commission',
      label: 'Commission',
      hasCheckmark: hasSavedState('leaseCommission') || hasCommissionData,
      progress: hasSavedState('leaseCommission') || hasCommissionData ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.commission]!,
      isVisible: true,
    },
    {
      name: PortfolioWizardStepNames.invoices,
      route: '/leases/:id/edit/invoices',
      label: 'Invoice templates',
      // Saving the Rent step auto-generates the rent/fee invoice templates, so the mere
      // presence of templates is not evidence the user has been through this step. While in
      // draft, only the step's own save (Next) earns the checkmark; a loaded (non-draft)
      // lease falls back to the portfolio's templates.
      hasCheckmark: hasSavedState('leaseInvoices') || (!isDraftPortfolio && hasInvoicesData),
      progress: (getLeaseInvoicesDialog(state)?.invoices || []).length > 0 ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.invoices]!,
      // Unmanaged leases only expose invoice templates while in draft; hide the
      // step once the lease is active as there are no monthly recurring invoices.
      isVisible: !isUnmanagedLease || isDraftPortfolio,
    },
    {
      name: PortfolioWizardStepNames.settings,
      route: '/leases/:id/edit/settings',
      label: 'Internal',
      // Nothing on this step is required — the form has no validation schema, and notes, the
      // custom lease identifier and segments are all optional. So there is no partial score to
      // give: the step is done once the user has been through it, which is the same signal the
      // checkmark uses. Scoring it on filled fields (as it once did) left the step stuck below
      // 100% — and therefore uncheckable — unless every optional field was completed.
      hasCheckmark: hasSavedState('leaseSettings') || hasSettingsData,
      progress: hasSavedState('leaseSettings') || hasSettingsData ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.settings]!,
      // No exception to sequential unlocking: the draft wizard is completed in step order,
      // so Internal is reached via Next off the Invoice templates step like any other step.
      isVisible: true,
    },
  ]

  if (isDraftPortfolio) {
    items.push({
      name: PortfolioWizardStepNames.activation,
      route: '/leases/:id/edit/activation',
      label: 'Activate',
      hasCheckmark: getLeaseConfirmationDialog(state).isActivated,
      progress: getLeaseConfirmationDialog(state).isActivated ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.activation]!,
      isVisible: true,
    })
  } else if (!isPortfolioTerminated) {
    items.push({
      name: PortfolioWizardStepNames.renewal,
      route: '/leases/:id/edit/renewal',
      label: 'Renewal',
      hasCheckmark: false,
      progress: getLeaseConfirmationDialog(state).isActivated ? 100 : 0,
      Icon: StaticStepIcons[PortfolioWizardStepNames.renewal]!,
      isVisible: true,
    })
    if (!isPortfolioExpired) {
      items.push({
        name: PortfolioWizardStepNames.termination,
        route: '/leases/:id/edit/termination',
        label: 'Termination',
        hasCheckmark: false,
        progress: getLeaseConfirmationDialog(state).isActivated ? 100 : 0,
        Icon: StaticStepIcons[PortfolioWizardStepNames.termination]!,
        isVisible: true,
      })
    }
  }

  const id = currentPortfolio || 'new'

  return items
    .map(
      ({
        name,
        route,
        label,
        hasCheckmark,
        isOptional,
        isAlwaysAccessible,
        progress,
        activeWhen = [],
        Icon,
        isVisible,
      }) => ({
        name,
        route: route.replace(':id', id),
        label,
        hasCheckmark,
        progress,
        isOptional,
        isAlwaysAccessible,
        activeWhen: map(activeWhen, (route: any) => route.replace(':id', id)),
        Icon,
        isVisible,
      }),
    )
    .map(
      ({
        name,
        route,
        label,
        hasCheckmark,
        isOptional,
        isAlwaysAccessible,
        activeWhen,
        progress,
        Icon,
        isVisible,
      }) => ({
        name,
        route,
        label,
        hasCheckmark,
        progress,
        isOptional,
        isAlwaysAccessible,
        isActive: (url: any) => activeWhen.concat([route]).includes(url),
        Icon,
        isVisible,
      }),
    )
    .filter(({ isVisible }) => isVisible)
})

export const dialogSavedStateSelector = createSelector(
  chatbotPortfolio,
  portfolio => (dialog: any) => portfolio.savedDialogs[dialog],
)

// ============================================================================
// Dialog-specific selectors (moved from legacy dialog files)
// ============================================================================

// --- LeaseTypeSelection selectors ---
export const getCurrentLeaseType = createSelector(getLeaseTypeSelectionDialog, dialog => dialog.leaseType)

// --- PropertyDetails selectors ---
export const getSelectedPlace = createSelector(getPropertyDetailsDialog, dialog => dialog.selectedPlace)
export const getSelectedProperty = createSelector(getPropertyDetailsDialog, dialog => dialog.selectedProperty)
export const getSelectedLightstoneProperty = createSelector(
  getPropertyDetailsDialog,
  dialog => dialog.selectedLightstoneProperty,
)
export const getPropertyDescription = createSelector(getPropertyDetailsDialog, dialog => dialog.propertyDescription)
export const getBuildingName = createSelector(getPropertyDetailsDialog, dialog => dialog.buildingName)
export const getUnitNumber = createSelector(getPropertyDetailsDialog, dialog => dialog.unitNumber)
export const getSchemeNumber = createSelector(getPropertyDetailsDialog, dialog => dialog.schemeNumber)
export const getSchemeName = createSelector(getPropertyDetailsDialog, dialog => dialog.schemeName)

export const isPlaceSelected = createSelector(getSelectedPlace, place => !!place)
export const isPropertySelected = createSelector(getPropertyDetailsDialog, dialog => !!dialog.selectedProperty)
export const isLightstonePropertySelected = createSelector(
  getPropertyDetailsDialog,
  dialog => !!dialog.selectedLightstoneProperty,
)

export const getPropertyInitialValues = createSelector(state, s => {
  const dialog = getPropertyDetailsDialog(s)
  const empty = {
    placeId: '',
    propertyId: '',
    propertyDescription: '',
    buildingName: '',
    unitNumber: '',
    schemeNumber: '',
    schemeName: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
  }

  const placeDetails = propertyApiSelectors.getGooglePlaceById(s)(dialog.selectedPlace)
  const fullProperty = propertyApiSelectors.getPropertyById(s)(dialog.selectedProperty)
  const lightstoneProperty = propertyApiSelectors.getLightstonePropertyById(s)(dialog.selectedLightstoneProperty)

  const portfolioId = getCurrentPortfolioId(s)
  const portfolioMeta = portfolioApiSelectors.getPortfolioMetaData(s)(portfolioId)

  const values = (details: any) => {
    const streetNumber = searchPlaceDetails(details, 'StreetNumber')
    const streetName = searchPlaceDetails(details, 'StreetName')
    const streetType = searchPlaceDetails(details, 'StreetType')
    const sublocality = searchPlaceDetails(details, 'Sublocality')
    const streetAddress = `${streetNumber} ${streetName} ${streetType}, ${sublocality}`
    const city = searchPlaceDetails(details, 'Locality')
    const province = searchPlaceDetails(details, 'AdministrativeArea', 1, undefined)
    const postalCode = searchPlaceDetails(details, 'PostalCode')
    const country = searchPlaceDetails(details, 'Country')
    const propertyDescription = portfolioMeta || getPropertyDescription(s)
    const buildingName = searchPlaceDetails(details, 'BuildingName', 1, undefined) || getBuildingName(s)
    const unitNumber = searchPlaceDetails(details, 'UnitNumber', 1, undefined) || getUnitNumber(s)
    const schemeNumber = searchPlaceDetails(details, 'SchemeNumber') || getSchemeNumber(s)
    const schemeName = searchPlaceDetails(details, 'SchemeName') || getSchemeName(s)

    return {
      placeId: details.placeId || '',
      propertyDescription,
      buildingName: buildingName ? titleCase(buildingName.replace('SS', '')).trim() : '',
      unitNumber: unitNumber === '0' ? '' : unitNumber,
      schemeNumber: schemeNumber === '0' ? '' : schemeNumber,
      schemeName: schemeName,
      streetAddress,
      city,
      province,
      postalCode,
      country,
    }
  }

  if (placeDetails !== undefined) {
    return values(placeDetails)
  }

  if (fullProperty !== undefined) {
    return values(fullProperty)
  }

  if (lightstoneProperty !== undefined) {
    return values(lightstoneProperty)
  }

  return empty
})

export const getChatbotSelector = createSelector(
  state,
  getPropertyDetailsDialog,
  (s, { selectedLightstoneProperty, selectedProperty, selectedPlace }) => {
    const portfolioId = getCurrentPortfolioId(s)
    const isInProgress = !portfolioId || getCurrentPortfolioStatus(s) === 'in progress'
    if (!selectedLightstoneProperty && !selectedProperty && !selectedPlace && !portfolioId) {
      return {
        step: getStep(PortfolioWizardStepNames.property),
        displayStep: isInProgress,
        title: 'Property',
        description: 'Search for and select the property this lease is linked to.',
        displayDescription: isInProgress,
        actions: [],
      }
    }
    return {
      step: getStep(PortfolioWizardStepNames.property),
      displayStep: isInProgress,
      title: 'Property',
      actions: [],
    }
  },
)

// --- OwnerDetails selectors ---
export const getSelectedOwner = createSelector(getOwnerDetailsDialog, dialog => dialog.selectedParty)

// --- TenantDetails selectors ---
export const getSelectedTenant = createSelector(getTenantDetailsDialog, dialog => dialog.selectedParty)

// --- LeaseTerm selectors ---
export const getLeaseTermInitialValues = createSelector(getLeaseTermDialog, ({ term }) => {
  if (term) {
    return {
      startDate: format(new Date(term.startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(term.endDate), 'yyyy-MM-dd'),
      rolloverMonthToMonth: term.rolloverMonthToMonth,
    }
  }

  const startDate = startOfMonth(addMonths(Date.now(), 1))
  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(lastDayOfMonth(addMonths(startDate, 11)), 'yyyy-MM-dd'),
    rolloverMonthToMonth: false,
  }
})

// --- LeaseRentAndFees selectors ---
export const getRentAmount = createSelector(getLeaseRentAndFeesDialog, dialog => get(dialog, 'monthlyRentAmount', 0))
export const getDamageDepositAmount = createSelector(getLeaseRentAndFeesDialog, dialog =>
  get(dialog, 'damageDepositAmount', 0),
)

// --- LeaseSettings selectors ---
export const getLeaseSettingsInitialValues = createSelector([state, getLeaseSettingsDialog], (s, dialogValues) => {
  const defaultSegmentId = userApiSelectors.getCurrentAgencyId(s)
  const portfolioId = getCurrentPortfolioId(s)
  const portfolioSegmentIds = portfolioApiSelectors
    .getSegmentIdsByPortfolioId(s)(portfolioId)
    .filter((id: any) => id !== defaultSegmentId)
  return {
    ...dialogValues,
    autoSendPaymentReminderSms:
      dialogValues.autoSendPaymentReminderSms !== null ? dialogValues.autoSendPaymentReminderSms : true,
    autoSendOwnerMonthlyEmail:
      dialogValues.autoSendOwnerMonthlyEmail !== null ? dialogValues.autoSendOwnerMonthlyEmail : true,
    segments: portfolioSegmentIds
      .map((id: any) => agencyApiSelectors.getSegmentById(s)(id))
      .filter((segment: any) => !!segment),
  }
})

// --- LeaseCommission selectors ---
// Persistent cache: the previous `createSelector(state, ...)` used the whole store as its
// memo input, so it produced a NEW initialValues object on every dispatch. That re-rendered
// the connected commission form (and re-ran Formik's enableReinitialize deep-compare) on every
// action, amplifying the wizard's render churn. The output depends only on the inputs listed
// below (all stable selector refs or primitives), so we return the cached object until one of
// them actually changes — keeping the reference stable across unrelated dispatches. The 4
// `vatable` flags are keyed individually because getCommissionByPortfolioId returns a fresh
// `{}` when a portfolio has no commission yet.
let commissionInitialValuesCache: { key: unknown[] | null; result: $TSFixMe } = { key: null, result: null }

export const getCommissionInitialValues = (s: $TSFixMe): $TSFixMe => {
  const { managementFee, procurementFee } = getLeaseCommissionDialog(s)
  const currentPortfolioId = getCurrentPortfolioId(s)
  const portfolioCommission = portfolioApiSelectors.getCommissionByPortfolioId(s)(currentPortfolioId)
  const globalVat = agencyApiSelectorsNamed.getCurrentAgencyGlobalVatEnabled(s)
  const monthlyRentAmount = getRentAmount(s)
  const currentAgencyId = userApiSelectors.getCurrentAgencyId(s)
  const rentInvoiceTemplateForKey = portfolioApiSelectors.getRentInvoiceTemplateByPortfolioId(s)(currentPortfolioId)

  const cacheKey = [
    getLeaseCommissionDialog(s),
    currentPortfolioId,
    globalVat,
    monthlyRentAmount,
    currentAgencyId,
    rentInvoiceTemplateForKey,
    partyApiSelectors.getParties(s),
    portfolioCommission?.managementFee?.netAmount?.vatable,
    portfolioCommission?.managementFee?.percentage?.vatable,
    portfolioCommission?.procurementFee?.netAmount?.vatable,
    portfolioCommission?.procurementFee?.percentage?.vatable,
  ]
  const prevKey = commissionInitialValuesCache.key
  if (prevKey && prevKey.length === cacheKey.length && prevKey.every((v, i) => v === cacheKey[i])) {
    return commissionInitialValuesCache.result
  }

  const result = ((): $TSFixMe => {
    // Seed the derived payment-rule totals so they match what ManagementFeeForm writes to Formik
    // on mount (via the currency/percentage fields' initial onChange). Without this the fields are
    // `undefined` in initialValues but set in values, leaving the form permanently `dirty`.
    const rentInvoiceTemplate = portfolioApiSelectors.getRentInvoiceTemplateByPortfolioId(s)(currentPortfolioId)
    const paymentRules = get(rentInvoiceTemplate, 'paymentRules', [])
    const commissionAmount =
      managementFee?.type === commissionUndertakings.fixed
        ? Number(managementFee?.netAmount?.value) || 0
        : (monthlyRentAmount / 100) * (Number(managementFee?.percentage?.value) || 0)
    const paymentRulesTotal = portfolioApiUtils.getPaymentRulesAmountExRent(paymentRules, commissionAmount)
    const paymentRulesTotalExCommission = portfolioApiUtils.getPaymentRulesAmountExRent(paymentRules, 0)

    const defaultSplit = currentAgencyId
      ? [
          {
            agentPartyId: currentAgencyId,
            splitPercentage: 100,
            isPrimary: true,
          },
        ]
      : []

    const managementSplits: $TSFixMe[] = get(managementFee, 'splits') || []
    const procurementSplits: $TSFixMe[] = get(procurementFee, 'splits') || []

    /** @todo this could be a reusable selector. Also used elsewhere */
    const shapeSplits = (splits: $TSFixMe) => {
      return splits.map((split: $TSFixMe) => {
        const { agentPartyId, splitPercentage, isPrimary } = split
        return {
          agent: {
            label: partyApiSelectors.getPartyNameById(s)(agentPartyId),
            value: agentPartyId,
          },
          splitPercentage,
          isPrimary,
        }
      })
    }

    return {
      managementFee: {
        ...managementFee,
        netAmount: {
          value: Number(managementFee?.netAmount?.value) || 0,
          vatable:
            typeof portfolioCommission?.managementFee?.netAmount?.vatable !== 'undefined'
              ? portfolioCommission?.managementFee?.netAmount?.vatable
              : globalVat,
        },
        percentage: {
          value: Number(managementFee?.percentage?.value) || 0,
          vatable:
            typeof portfolioCommission?.managementFee?.percentage?.vatable !== 'undefined'
              ? portfolioCommission?.managementFee?.percentage?.vatable
              : globalVat,
        },
        monthlyRentAmount,
        paymentRulesTotal,
        paymentRulesTotalExCommission,
        splits: shapeSplits(managementSplits.length > 0 ? managementSplits : defaultSplit),
      },
      procurementFee: {
        ...procurementFee,
        netAmount: {
          value: Number(procurementFee?.netAmount?.value) || 0,
          vatable:
            typeof portfolioCommission?.procurementFee?.netAmount?.vatable !== 'undefined'
              ? portfolioCommission?.procurementFee?.netAmount?.vatable
              : globalVat,
        },
        percentage: {
          value: Number(procurementFee?.percentage?.value) || 0,
          vatable:
            typeof portfolioCommission?.procurementFee?.percentage?.vatable !== 'undefined'
              ? portfolioCommission?.procurementFee?.percentage?.vatable
              : globalVat,
        },
        monthlyRentAmount,
        paymentRulesTotal,
        paymentRulesTotalExCommission,
        splits: shapeSplits(procurementSplits.length > 0 ? procurementSplits : defaultSplit),
      },
    }
  })()

  commissionInitialValuesCache = { key: cacheKey, result }
  return result
}

// --- LeaseInvoices selectors ---
export const getInvoicesInitialValues = createSelector(state, getLeaseInvoicesDialog, (s, dialog) => {
  const invoices: any[] = []
  const currentPortfolioId = getCurrentPortfolioId(s)
  const portfolioStatus = portfolioApiSelectors.getPortfolioStatusLabel(s)(currentPortfolioId)

  dialog.invoices.forEach((inv: any) => {
    const { id, invoicePartyId, invoicePartyAccountType, category, interval, vatable, paymentRules, description } = inv
    const ownerId = getSelectedOwner(s)
    const tenantId = getSelectedTenant(s)

    let _invoicePartyAccountType = invoicePartyAccountType

    if (!_invoicePartyAccountType) {
      _invoicePartyAccountType = invoicePartyId === ownerId ? 'Owner' : invoicePartyId === tenantId ? 'Tenant' : null
    }

    if (_invoicePartyAccountType === null) {
      throw new Error('Invalid invoicePartyAccountType')
    }

    invoices.push({
      id,
      editing: false,
      autoDeliver: true,
      category,
      invoicePartyId,
      invoicePartyAccountType: _invoicePartyAccountType,
      interval,
      netAmount: inv.netAmount,
      vatable,
      paymentRules: paymentRules.map((pr: any) => {
        /** @todo update/api - implement `type`s */
        const { beneficiary, clearance } = pr
        const name = partyApiSelectors.getPartyNameById(s)(beneficiary.value.partyId)
        return {
          beneficiary: {
            ...beneficiary,
            value: {
              ...beneficiary.value,
              name,
              amount: getDepositBeneficiaryAmount({
                invoiceType: category,
                invoiceAmount: inv.netAmount,
                beneficiaryType: beneficiary.type,
                amount: beneficiary.value.amount,
              }),
            },
          },
          clearance,
        }
      }),
      description,
    })
  })

  return { invoices: invoices.filter(inv => (portfolioStatus !== 'draft' ? inv.interval !== 'OnceOff' : true)) }
})

export const getInvoices = createSelector(getLeaseInvoicesDialog, dialog => dialog.invoices)

export const getRemovedInvoices = createSelector(getLeaseInvoicesDialog, dialog => dialog.removedInvoiceIds)

export const getUpdatedInvoiceIds = createSelector(getLeaseInvoicesDialog, dialog => dialog.updatedInvoiceIds)

// --- UserDetails selectors ---
export const getCurrentUserDetailsInitialValues = createSelector(
  state,
  getCurrentUserDetailsDialog,
  (s, dialogValues) => ({
    ...dialogValues,
    emailAddress: userApiSelectors.getUserEmail(s),
    cellVerified: userApiSelectors.cellVerified(s),
    emailVerified: userApiSelectors.emailVerified(s),
  }),
)

// --- AgencyDetails selectors ---
export const getAgencyDetailsInitialValues = createSelector(
  state,
  getAgencyDetailsDialog,
  (s): TAgencyProfileFormValues => {
    const agency = agencyApiSelectorsNamed.getCurrentAgency(s)
    // See `uiSettingsSelectors.getProfileInitialValues` — the stored profile is
    // partial on the wire but the form consumes it as complete.
    return (agency?.agency as TAgencyProfileFormValues | undefined) ?? EMPTY_AGENCY_PROFILE_FORM_VALUES
  },
)
